import React from 'react';
import { InsightsResponse } from '~/lib/api.ts';

interface TodayScreenProps {
  insights: InsightsResponse | null;
  onBack: () => void;
}

export const TodayScreen: React.FC<TodayScreenProps> = ({ insights, onBack }) => {
  const today = insights?.today;
  const week = insights?.week;

  const getProgressPercentage = (current: number | null, target: number) => {
    if (!current) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return '#4CAF50';
    if (percentage >= 60) return '#FF9800';
    return '#F44336';
  };

  const formatSteps = (steps: number | null) => {
    if (!steps) return '0';
    return steps.toLocaleString();
  };

  const formatSleep = (hours: number | null) => {
    if (!hours) return '0ч';
    return `${hours.toFixed(1)}ч`;
  };

  const formatScreenTime = (minutes: number | null) => {
    if (!minutes) return '0ч';
    const hours = minutes / 60;
    return `${hours.toFixed(1)}ч`;
  };

  const getWeeklySparkline = () => {
    // Простая sparkline для демонстрации
    return '▁▂▃▄▅▆▇';
  };

  return (
    <div className="screen">
      <div className="header">
        <button className="back-button" onClick={onBack}>
          ← Назад
        </button>
        <h1>📊 Сегодня</h1>
      </div>

      {today && (
        <div className="today-stats">
          <div className="stat-progress">
            <div className="stat-header">
              <span className="stat-icon">😴</span>
              <span className="stat-label">Сон</span>
              <span className="stat-value">{formatSleep(today.sleep_hours)}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${getProgressPercentage(today.sleep_hours, 8)}%`,
                  backgroundColor: getProgressColor(getProgressPercentage(today.sleep_hours, 8))
                }}
              />
            </div>
            <span className="progress-target">Цель: 8ч</span>
          </div>

          <div className="stat-progress">
            <div className="stat-header">
              <span className="stat-icon">👟</span>
              <span className="stat-label">Шаги</span>
              <span className="stat-value">{formatSteps(today.steps)}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${getProgressPercentage(today.steps, 8000)}%`,
                  backgroundColor: getProgressColor(getProgressPercentage(today.steps, 8000))
                }}
              />
            </div>
            <span className="progress-target">Цель: 8,000</span>
          </div>

          <div className="stat-progress">
            <div className="stat-header">
              <span className="stat-icon">📱</span>
              <span className="stat-label">Экранное время</span>
              <span className="stat-value">{formatScreenTime(today.screen_time_minutes)}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${getProgressPercentage(today.screen_time_minutes, 180)}%`,
                  backgroundColor: getProgressColor(getProgressPercentage(today.screen_time_minutes, 180))
                }}
              />
            </div>
            <span className="progress-target">Цель: ≤3ч</span>
          </div>

          <div className="stat-progress">
            <div className="stat-header">
              <span className="stat-icon">😊</span>
              <span className="stat-label">Настроение</span>
              <span className="stat-value">{today.mood_score || '—'}/5</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${getProgressPercentage(today.mood_score, 5)}%`,
                  backgroundColor: getProgressColor(getProgressPercentage(today.mood_score, 5))
                }}
              />
            </div>
            <span className="progress-target">Цель: 5/5</span>
          </div>
        </div>
      )}

      {week && (
        <div className="weekly-summary">
          <h3>📈 За неделю</h3>
          <div className="weekly-stats">
            <div className="weekly-stat">
              <span className="weekly-label">Всего шагов:</span>
              <span className="weekly-value">{week.total_steps.toLocaleString()}</span>
              <span className="weekly-sparkline">{getWeeklySparkline()}</span>
            </div>
            <div className="weekly-stat">
              <span className="weekly-label">Средний сон:</span>
              <span className="weekly-value">{week.avg_sleep.toFixed(1)}ч</span>
            </div>
            <div className="weekly-stat">
              <span className="weekly-label">Среднее настроение:</span>
              <span className="weekly-value">{week.avg_mood.toFixed(1)}/5</span>
            </div>
            <div className="weekly-stat">
              <span className="weekly-label">Дней записано:</span>
              <span className="weekly-value">{week.days_logged}/7</span>
            </div>
          </div>
        </div>
      )}

      {insights?.tips && insights.tips.length > 0 && (
        <div className="today-tips">
          <h3>💡 Советы на сегодня</h3>
          <div className="tips-list">
            {insights.tips.map((tip, index) => (
              <div key={index} className="tip-item">
                {tip}
              </div>
            ))}
          </div>
        </div>
      )}

      {today?.is_complete && (
        <div className="completion-badge">
          🎉 Отличный день! Все метрики записаны!
        </div>
      )}
    </div>
  );
};
